import type {
  LlmAttachment,
  LlmAttachmentMimeType,
  LlmModelId,
  LlmProvider,
  ModelConfig,
} from '../../services/llm';

const ANTHROPIC_EFFORTS = ['low', 'medium', 'high'];
const OPENAI_5_4_EFFORTS = ['low', 'medium', 'high', 'xhigh'];
const OPENAI_EFFORTS = ['minimal', 'low', 'medium', 'high'];
const GEMINI_3X_EFFORTS = ['minimal', 'low', 'medium', 'high'];
const GEMINI_3_PRO_EFFORTS = ['low', 'medium', 'high'];
const GEMINI_3_FLASH_37_EFFORTS = ['low', 'medium', 'high'];

/** UI-safe capability metadata. Provider SDKs and Firebase clients are not
 * imported here; the server remains authoritative for execution. */
export const MODEL_CONFIGS: Record<LlmModelId, ModelConfig> = {
  'claude-opus-5': { provider: 'anthropic', thinkingMode: 'adaptive', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-opus-4-7': { provider: 'anthropic', thinkingMode: 'adaptive', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-sonnet-4-6': { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-haiku-4-5': { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-opus-4-6': { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-sonnet-4-5': { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'gpt-5.6-sol': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.5': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.5-pro': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4-pro': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4-mini': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4-nano': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_EFFORTS] },
  'gpt-5-mini': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_EFFORTS] },
  'gpt-5-nano': { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_EFFORTS] },
  'moonshotai/Kimi-K3': { provider: 'together', thinkingMode: 'always', supportedEffortLevels: ['low', 'high', 'max'] },
  'zai-org/GLM-5.2': { provider: 'together', thinkingMode: 'always', supportedEffortLevels: ['high', 'max'] },
  'MiniMaxAI/MiniMax-M2.7': { provider: 'together', thinkingMode: 'none', supportedEffortLevels: null },
  'gemini-3.7-flash': { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3_FLASH_37_EFFORTS] },
  'gemini-3.1-pro-preview': { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3_PRO_EFFORTS] },
  'gemini-3-flash-preview': { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3X_EFFORTS] },
  'gemini-3.1-flash-lite': { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3X_EFFORTS] },
  'gemini-2.5-pro': { provider: 'gemini', thinkingMode: 'optional', supportedEffortLevels: null },
};

export function getModelConfig(id: LlmModelId): ModelConfig {
  return MODEL_CONFIGS[id];
}

export function getProviderForModel(id: LlmModelId): LlmProvider {
  return MODEL_CONFIGS[id].provider;
}

export function getSupportedEffortLevels(id: LlmModelId): string[] | null {
  return MODEL_CONFIGS[id].supportedEffortLevels;
}

export function effortIsActive(id: LlmModelId, thinkingEnabled: boolean): boolean {
  const cfg = MODEL_CONFIGS[id];
  if (cfg.supportedEffortLevels === null) return false;
  if (cfg.thinkingMode === 'always') return true;
  if (cfg.thinkingMode === 'optional') return thinkingEnabled;
  return false;
}

const ALLOWED_ATTACHMENT_MIME_TYPES: LlmAttachmentMimeType[] = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain',
];

export const LLM_ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;

export async function fileToLlmAttachment(file: File): Promise<LlmAttachment> {
  if (file.size > LLM_ATTACHMENT_MAX_BYTES) {
    throw new Error(`Attachment "${file.name}" is over the ${LLM_ATTACHMENT_MAX_BYTES}-byte limit.`);
  }
  if (!ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type as LlmAttachmentMimeType)) {
    throw new Error(`Attachment "${file.name}" has unsupported mime type.`);
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return { mimeType: file.type as LlmAttachmentMimeType, data: btoa(binary), name: file.name };
}
